/**
 * Skeleton loaders — Rijschool AutoPilot.
 * A base <SkeletonBlock/> primitive + composite skeletons that mimic the
 * real layout of Dashboard, StudentList, LessonScheduler and LessonCard.
 */

export function SkeletonBlock({ className = '', style, tone = 'default', ...props }) {
    const toneClass = tone === 'dark' ? 'skeleton-dark' : tone === 'light' ? 'skeleton-light' : '';
    return (
        <div
            className={`skeleton ${toneClass} ${className}`}
            style={style}
            aria-hidden
            {...props}
        />
    );
}

export function SkeletonCircle({ size = 40, className = '' }) {
    return (
        <SkeletonBlock
            className={`rounded-full ${className}`}
            style={{ width: size, height: size }}
        />
    );
}

export function SkeletonLine({ w = '100%', h = 12, className = '' }) {
    return (
        <SkeletonBlock
            className={className}
            style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }}
        />
    );
}

/* ---------- Composite: student list row ---------- */
export function StudentRowSkeleton() {
    return (
        <div
            className="p-4 border-b border-[#4A5568] flex items-center justify-between gap-4"
            style={{ boxShadow: 'inset 4px 0 0 rgba(212,165,55,0.25)' }}
            data-testid="student-row-skeleton"
        >
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <SkeletonCircle size={48} />
                <div className="flex-1 space-y-2 min-w-0">
                    <SkeletonLine w={160} h={14} />
                    <div className="flex gap-2">
                        <SkeletonLine w={70} h={10} tone="dark" />
                        <SkeletonLine w={90} h={10} tone="dark" />
                    </div>
                </div>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-1.5 w-40">
                <SkeletonLine w={40} h={10} tone="dark" />
                <SkeletonLine w="100%" h={6} />
            </div>
        </div>
    );
}

export function StudentListSkeleton({ rows = 4 }) {
    return (
        <div className="divide-y divide-[#4A5568]" data-testid="student-list-skeleton">
            {Array.from({ length: rows }).map((_, i) => (
                <StudentRowSkeleton key={i} />
            ))}
        </div>
    );
}

/* ---------- Composite: stat quick-action tile ---------- */
export function QuickActionSkeleton() {
    return (
        <div className="bg-[#2D3748] rounded-2xl border border-[#4A5568] p-5 flex items-start gap-3">
            <SkeletonCircle size={40} />
            <div className="flex-1 space-y-2">
                <SkeletonLine w={90} h={14} />
                <SkeletonLine w={130} h={10} tone="dark" />
            </div>
        </div>
    );
}

/* ---------- Composite: Home tab quick-actions grid ---------- */
export function HomeQuickActionsSkeleton({ count = 5 }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: count }).map((_, i) => (
                <QuickActionSkeleton key={i} />
            ))}
        </div>
    );
}

/* ---------- Composite: Dashboard full-screen loader ---------- */
export function DashboardSkeleton() {
    return (
        <div className="min-h-screen bg-[#1A202C]" data-testid="dashboard-skeleton">
            {/* Header */}
            <div className="bg-[#2D3748] border-b border-[#4A5568] px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <SkeletonCircle size={36} />
                    <SkeletonLine w={100} h={14} />
                </div>
                <div className="flex items-center gap-2">
                    <SkeletonCircle size={32} />
                    <SkeletonCircle size={32} />
                    <SkeletonCircle size={32} />
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 space-y-6">
                {/* Greeting */}
                <div className="space-y-2">
                    <SkeletonLine w={260} h={28} />
                    <SkeletonLine w={180} h={12} tone="dark" />
                </div>

                {/* Quick action tiles */}
                <div className="space-y-3">
                    <SkeletonLine w={100} h={10} tone="dark" />
                    <HomeQuickActionsSkeleton count={5} />
                </div>

                {/* Next lesson card */}
                <div className="bg-[#2D3748] rounded-2xl border border-[#4A5568] p-5 space-y-3">
                    <SkeletonLine w={140} h={11} tone="dark" />
                    <SkeletonLine w="60%" h={22} />
                    <div className="flex gap-3">
                        <SkeletonLine w={120} h={12} tone="dark" />
                        <SkeletonLine w={90} h={12} tone="dark" />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------- Composite: Lesson card skeleton (matches mobile mockup style) ---------- */
export function LessonCardSkeleton() {
    return (
        <div
            className="relative bg-[#2D3748] rounded-2xl overflow-hidden p-4 pl-5"
            data-testid="lesson-card-skeleton"
        >
            <span
                aria-hidden
                className="absolute inset-y-3 left-0 w-1 rounded-r-full skeleton skeleton-light"
            />
            <div className="flex items-start justify-between gap-3">
                <SkeletonLine w={110} h={14} />
                <div className="flex gap-1.5">
                    <SkeletonLine w={44} h={18} />
                    <SkeletonLine w={64} h={18} />
                </div>
            </div>
            <div className="mt-3 space-y-2">
                <SkeletonLine w="70%" h={18} />
                <SkeletonLine w={90} h={12} tone="dark" />
            </div>
        </div>
    );
}

export function LessonListSkeleton({ count = 3 }) {
    return (
        <div className="space-y-2.5" data-testid="lesson-list-skeleton">
            {Array.from({ length: count }).map((_, i) => (
                <LessonCardSkeleton key={i} />
            ))}
        </div>
    );
}

/* ---------- Composite: Agenda (LessonScheduler) full skeleton ---------- */
export function AgendaSkeleton() {
    return (
        <div className="space-y-4 pb-24" data-testid="agenda-skeleton">
            {/* View mode tabs */}
            <div className="grid grid-cols-3 gap-2 bg-[#1A202C] rounded-2xl p-1.5 border border-[#4A5568]">
                {[0, 1, 2].map(i => (
                    <SkeletonBlock key={i} className="h-10 rounded-xl" tone={i === 0 ? 'light' : 'default'} />
                ))}
            </div>

            {/* Month header + week strip */}
            <div className="bg-[#1A202C] rounded-2xl border border-[#4A5568] p-4">
                <div className="flex items-center justify-between mb-3">
                    <SkeletonCircle size={30} />
                    <SkeletonLine w={140} h={18} />
                    <SkeletonCircle size={30} />
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex justify-center">
                            <SkeletonLine w={18} h={10} tone="dark" />
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex justify-center py-1.5">
                            <SkeletonCircle size={44} />
                        </div>
                    ))}
                </div>
                <div className="mt-3 mx-auto w-14 h-1 rounded-full bg-[#4A5568]/70" aria-hidden />
            </div>

            {/* CTAs */}
            <div className="space-y-2">
                <SkeletonBlock className="w-full h-12 rounded-2xl" tone="light" />
                <SkeletonBlock className="w-full h-12 rounded-2xl" />
            </div>

            {/* Section label + cards */}
            <div className="flex items-center justify-between px-1">
                <SkeletonLine w={180} h={10} tone="dark" />
                <SkeletonLine w={100} h={10} tone="dark" />
            </div>
            <LessonListSkeleton count={3} />
        </div>
    );
}
